/** A multi-line control, with an optional character count against its own `maxLength`. */

import { useState, type ChangeEvent, type Ref, type TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

import { mergeStyle } from "../internal/style";
import "../internal/control.css";
import "./Textarea.css";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean;
  /** Show the length against `maxLength`. */
  readonly counter?: boolean;
  readonly resize?: "none" | "vertical" | "both";
  readonly ref?: Ref<HTMLTextAreaElement>;
}

function lengthOfValue(value: unknown): number {
  return typeof value === "string" ? value.length : 0;
}

export function Textarea({
  invalid,
  counter,
  resize = "vertical",
  className,
  style,
  onChange,
  ...rest
}: TextareaProps) {
  const [typed, setTyped] = useState(() =>
    lengthOfValue(rest.value ?? rest.defaultValue ?? ""),
  );
  const used = rest.value === undefined ? typed : lengthOfValue(rest.value);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTyped(event.target.value.length);
    onChange?.(event);
  };

  return (
    <div className="al-textarea-wrap">
      <textarea
        aria-invalid={invalid === true ? true : undefined}
        className={cn(
          "al-control",
          "al-textarea",
          invalid === true ? "al-control-invalid" : undefined,
          className,
        )}
        style={mergeStyle(style, { resize })}
        onChange={handleChange}
        {...rest}
      />
      {counter === true && rest.maxLength !== undefined ? (
        <div className="al-textarea-count al-tabular">
          {used} / {rest.maxLength}
        </div>
      ) : null}
    </div>
  );
}
